import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import type { UserRole } from '@sync-tracker/shared';
import { setUnauthorizedHandler } from './api/http';
import { baseTitle } from './lib/tabBadge';
import { useAuth } from './stores/auth';

declare module 'vue-router' {
  interface RouteMeta {
    role?: UserRole;
    public?: boolean;
    title?: string;
    bare?: boolean;
  }
}

// Страницы подгружаются лениво; glob вместо прямых import() — чтобы сборка не падала из-за одной страницы
const pages = import.meta.glob<{ default: unknown }>('./pages/*.vue');
const page = (name: string) => pages[`./pages/${name}.vue`] ?? pages['./pages/BoardPage.vue']!;

const routes: RouteRecordRaw[] = [
  { path: '/login', name: 'login', component: page('LoginPage'), meta: { public: true, bare: true, title: 'Вход' } },
  { path: '/', redirect: '/board' },
  { path: '/board', name: 'board', component: page('BoardPage'), meta: { title: 'Доска' } },
  { path: '/tickets', name: 'tickets', component: page('TicketsPage'), meta: { title: 'История' } },
  { path: '/chats', name: 'chats', component: page('ChatsPage'), meta: { title: 'Чаты' } },
  { path: '/groups', name: 'groups', component: page('GroupsPage'), meta: { title: 'Создание групп', role: 'operator' } },
  { path: '/invite', name: 'invite', component: page('InvitePage'), meta: { title: 'Добавить в чат' } },
  { path: '/broadcasts', name: 'broadcasts', component: page('BroadcastsPage'), meta: { title: 'Рассылки' } },
  { path: '/my-columns', name: 'my-columns', component: page('MyColumnsPage'), meta: { title: 'Мои колонки', role: 'operator' } },
  { path: '/analytics', name: 'analytics', component: page('AnalyticsPage'), meta: { title: 'Аналитика' } },
  { path: '/directory', name: 'directory', component: page('DirectoryPage'), meta: { title: 'Справочники' } },
  { path: '/settings', name: 'settings', component: page('SettingsPage'), meta: { title: 'Настройки', role: 'admin' } },
  { path: '/users', name: 'users', component: page('UsersPage'), meta: { title: 'Пользователи', role: 'admin' } },
  { path: '/audit', name: 'audit', component: page('AuditPage'), meta: { title: 'Аудит', role: 'admin' } },
  { path: '/profile', name: 'profile', component: page('ProfilePage'), meta: { title: 'Профиль' } },
  { path: '/:pathMatch(.*)*', redirect: '/board' },
];

export const router = createRouter({ history: createWebHistory(), routes });

router.beforeEach(async (to) => {
  const auth = useAuth();
  if (!auth.checked) await auth.check();
  if (to.meta.public) return auth.user && to.name === 'login' ? { path: '/board' } : true;
  if (!auth.user) return { name: 'login', query: to.fullPath !== '/board' ? { next: to.fullPath } : {} };
  if (to.meta.role && !auth.can(to.meta.role)) return { path: '/board' };
  return true;
});

router.afterEach((to) => {
  baseTitle.value = document.title = to.meta.title ? `${to.meta.title} · Sync Tracker` : 'Sync Tracker';
});

setUnauthorizedHandler(() => {
  const auth = useAuth();
  auth.user = null;
  if (router.currentRoute.value.name !== 'login') void router.push({ name: 'login', query: { next: router.currentRoute.value.fullPath } });
});
